import { Button, Card, Form, Input, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Enrollment = { id: string; record_version: number };

export default function WithdrawalPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form] = Form.useForm();

  const enrollment = useQuery({
    queryKey: ['education-enrollment', id],
    enabled: Boolean(id),
    queryFn: () => api.get<Enrollment>(`/education/enrollments/${id}`),
  });

  useEffect(() => {
    const version = enrollment.data?.data?.record_version;
    if (version != null) form.setFieldValue('record_version', version);
  }, [enrollment.data?.data?.record_version, form]);

  const request = useMutation({
    mutationFn: (values: Record<string, string | number>) =>
      api.post(`/education/enrollments/${id}/withdrawal`, {
        ...values,
        record_version: values.record_version ?? enrollment.data?.data?.record_version,
      }),
    onSuccess: () => message.success(t('withdrawalRequested')),
  });

  return (
    <EducationPageChrome
      title={t('withdrawal')}
      description={t('withdrawalIntro')}
      extra={
        <Button onClick={() => navigate(`/education/enrollments/${id}?${params.toString()}`)}>
          {t('backToEnrollments')}
        </Button>
      }
    >
      <Card size="small" loading={enrollment.isLoading}>
        <Form form={form} layout="vertical" onFinish={(values) => request.mutate(values)} style={{ maxWidth: 480 }}>
          <Form.Item name="request_date" label={t('requestDate')} rules={[{ required: true }]}>
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="requested_effective_on" label={t('effectiveOn')} rules={[{ required: true }]}>
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="reason_category" label={t('reason')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="guardian_reference" label={t('guardianReference')}>
            <Input />
          </Form.Item>
          <Form.Item name="record_version" hidden>
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={request.isPending}>{t('save')}</Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
